import React from "react";
import { Tooltip } from "@mui/material";
import ViewListRoundedIcon from "@mui/icons-material/ViewListRounded";
import GridViewRoundedIcon from "@mui/icons-material/GridViewRounded";
import ViewSidebarRoundedIcon from "@mui/icons-material/ViewSidebarRounded";
import { cn } from "../../lib/cn";

export type HubView = "list" | "grid" | "detail";

export interface ViewSegmentedProps {
  value: HubView;
  onChange: (v: HubView) => void;
  options?: HubView[];
}

const ICONS: Record<HubView, React.ReactNode> = {
  list: <ViewListRoundedIcon />,
  grid: <GridViewRoundedIcon />,
  detail: <ViewSidebarRoundedIcon />,
};

const LABELS: Record<HubView, string> = {
  list: "List",
  grid: "Grid",
  detail: "Detail",
};

/**
 * Segmented List / Grid / Detail control. Mirrors `.viewseg` from
 * docs/redesign/hub-redesign.css.
 *
 * The selected segment uses the shared `.seg-on` class (src/tailwind.css), the
 * same treatment as ExploreToolbar / ProjToolbar / TracksTabs. It is neutral by
 * design — `bg-accent-grad` is reserved for filter chips and CTAs, so a mode
 * toggle never competes with a content selection for attention.
 */
export const ViewSegmented: React.FC<ViewSegmentedProps> = ({
  value,
  onChange,
  options = ["list", "grid", "detail"],
}) => (
  <div className="inline-flex items-center bg-surface border border-border rounded-sm p-[3px] gap-[3px] shadow-edge-hi">
    {options.map((opt) => {
      const active = opt === value;
      return (
        <Tooltip key={opt} title={LABELS[opt]}>
          <button
            type="button"
            aria-label={LABELS[opt]}
            aria-pressed={active}
            onClick={() => onChange(opt)}
            className={cn(
              "appearance-none p-0 cursor-pointer grid place-items-center w-8 h-[30px] rounded-[7px]",
              "transition-all duration-150 ease-[cubic-bezier(0.16,1,0.3,1)] active:scale-[0.96]",
              "[&_svg]:w-[18px] [&_svg]:h-[18px]",
              active
                ? "seg-on"
                : "border border-transparent bg-transparent text-muted hover:text-body hover:bg-surface-hi",
            )}
          >
            {ICONS[opt]}
          </button>
        </Tooltip>
      );
    })}
  </div>
);

export default ViewSegmented;
